import { useMemo } from 'react';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { AppointmentRow, LeadRow } from '@/lib/types';
import { initials, formatDate, fullName } from '@/lib/format';

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-amber-100 text-amber-700',
  interested: 'bg-green-100 text-green-700',
  'follow-up': 'bg-purple-100 text-purple-700',
  converted: 'bg-teal-100 text-teal-700',
  lost: 'bg-red-100 text-red-700',
};

function apptTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function PanelsRow() {
  const { data: appts, loading: apptLoading } = useApiData<AppointmentRow[]>(() => api.appointments());
  const { data: leads, loading: leadLoading } = useApiData<LeadRow[]>(() => api.leads());

  const upcoming = useMemo(
    () => (appts ?? [])
      .filter((a) => new Date(a.startsAt).getTime() >= Date.now() - 3600_000)
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
      .slice(0, 5),
    [appts],
  );
  const recentLeads = useMemo(() => (leads ?? []).slice(0, 5), [leads]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Upcoming Appointments */}
      <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E1E5EA]">
          <h3 className="text-sm font-semibold text-[#3C3C3C]">Upcoming Appointments</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {apptLoading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
          ) : upcoming.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No upcoming appointments</div>
          ) : upcoming.map((appt) => (
            <div key={appt.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#0B6E77] text-white text-xs font-bold flex-shrink-0">
                {initials(appt.studentName || appt.title)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{appt.studentName || appt.title}</p>
                <p className="text-xs text-gray-400 truncate">{appt.counselorName || 'Unassigned'}{appt.type ? ` · ${appt.type}` : ''}</p>
              </div>
              <span className="text-xs font-semibold text-[#3C3C3C] whitespace-nowrap">{apptTime(appt.startsAt)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E1E5EA]">
          <h3 className="text-sm font-semibold text-[#3C3C3C]">Recent Leads</h3>
        </div>
        <div className="divide-y divide-gray-50">
          {leadLoading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Loading…</div>
          ) : recentLeads.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">No leads yet</div>
          ) : recentLeads.map((lead) => {
            const name = fullName(lead.first_name, lead.last_name);
            return (
              <div key={lead.id} className="flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex-shrink-0">
                  {initials(name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{name}</p>
                  <p className="text-xs text-gray-400 truncate">{lead.source || '—'} · {formatDate(lead.created_at)}</p>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${statusColors[(lead.status || '').toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>
                  {lead.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
